import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { PictureComponent } from '../picture/picture.component';
import { HelpersService } from '../../services/helpers.service';
import { CommonModule } from '@angular/common';

export interface HeroDataItem {
  backgroundImageDesktop: string;
  titleText: string;
  titleColorText: string;
  bodyText: string;
  bodyTextDesktopWidth: string;
  sillHouetteImage: string;
  sillhouette: {
    mobile: {
      width: number;
      top: number;
      left: number;
    };
    tablet?: {
      width: number;
      top: number;
      left: number;
    };
    desktop: {
      width: number;
      height: number;
      top: number;
      left: number;
    };
  };
  sillhouetteAlt?: string;
  altText?: string;
}

@Component({
  selector: '[global-hero]',
  imports: [CommonModule, PictureComponent],
  templateUrl: './global-hero.component.html',
  styleUrl: './global-hero.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'position-relative z-0',
    '(window:resize)': 'onWindowResize()',
  },
})
export class GlobalHeroComponent implements OnInit, AfterViewChecked {
  helpersService = inject(HelpersService);
  renderer = inject(Renderer2);

  isMobile: boolean = false;
  isMobileLayout: boolean = false;
  mobileLayout = 390;
  tabletBreakpoint = 768; //tablet = 768, desktop = 1024
  desktopBreakpoint = 1024;
  isTablet: boolean = false;
  breakpoint = 'md'; //tablet = 'md', desktop = 'lg'
  //this is to adjust sillhouette in smaller screens
  isDesktopXLgLayout: boolean = false;
  isDesktopLgLayout: boolean = false;
  desktopLgBreakpoint = 1150;
  desktopXLBreakpoint = 1340;
  offsetX: number = 0;

  //custom variables
  @Input() heroData: HeroDataItem = {
    backgroundImageDesktop: '',
    titleText: '',
    titleColorText: '',
    bodyText: '',
    bodyTextDesktopWidth: '',
    sillHouetteImage: '',
    sillhouette: {
      mobile: {
        width: 0,
        top: 0,
        left: 0,
      },
      tablet: {
        width: 0,
        top: 0,
        left: 0,
      },
      desktop: {
        width: 0,
        height: 0,
        top: 0,
        left: 0,
      },
    },
    sillhouetteAlt: '',
  };

  backgroundImgPath!: string;
  backgroundImgPosition!: string;

  //sillhouette
  @ViewChild('sillhouette')
  sillhouette?: ElementRef<HTMLElement>;
  sillhouetteData?: any;

  ngOnInit(): void {
    //validate platform
    if (!this.helpersService.validatePlatform) return;

    //validate viewport
    this.isMobileLayout = this.helpersService.isMobile(this.mobileLayout);
    this.isMobile = this.helpersService.isMobile(this.tabletBreakpoint);
    this.isTablet = this.helpersService.isMobile(this.desktopBreakpoint);
    this.isDesktopXLgLayout = this.helpersService.isMobile(
      this.desktopLgBreakpoint
    );
    this.isDesktopLgLayout = this.helpersService.isMobile(
      this.desktopXLBreakpoint
    );

    //load slide imgs
    this.loadSlide();
  }

  ngAfterViewChecked(): void {
    //set up sillhouette
    this.setSillhouetteProps();
  }

  loadSlide(): void {
    const mobSuffix = this.isMobile ? '_s' : this.isTablet ? '_m' : '';
    const imgExt = this.helpersService.canUseWebp() ? 'webp' : 'png';
    this.backgroundImgPath = `url(/assets/img/global-hero-images/${this.heroData.backgroundImageDesktop}-bg${mobSuffix}.${imgExt})`;

    //if desktop, validate if screen is less
    //than 1300px
    this.offsetX =
      this.heroData.backgroundImageDesktop == 'hero-request-screening' &&
      this.isDesktopLgLayout
        ? -50
        : this.isDesktopXLgLayout
        ? -150
        : this.isDesktopLgLayout
        ? -100
        : 0;
    this.backgroundImgPosition =
      this.isDesktopLgLayout && !this.isTablet && !this.isMobile
        ? `left ${this.offsetX}px top 0`
        : '';

    //load sillhouette picture
    this.sillhouetteData = {
      image:
        'global-hero-images/' +
        this.heroData.sillHouetteImage +
        (!this.isMobile && this.isTablet ? '_m' : ''),
      format: 'png',
      altext: this.heroData.sillhouetteAlt,
      mobile: this.heroData.sillhouette.mobile && this.isMobile,
      webp: true,
      fill: false,
      width: this.heroData.sillhouette.desktop?.width,
      height: this.heroData.sillhouette.desktop.height,
      classes: 'img-fluid w-100',
    };
  }

  setSillhouetteProps(): void {
    if (!this.sillhouette) return;

    //set custom props to sillhouette
    let type = this.isMobile
      ? this.heroData.sillhouette.mobile
      : this.isTablet
      ? this.heroData.sillhouette.tablet
      : this.heroData.sillhouette.desktop;

    let width = this.isMobile
      ? this.helpersService.toVW(type?.width || 0, this.mobileLayout)
      : this.isTablet
      ? this.helpersService.toVW(type?.width || 0, this.tabletBreakpoint)
      : this.helpersService.toREM(type?.width || 0);
    this.renderer.setStyle(this.sillhouette?.nativeElement, 'width', width);

    let top = this.isMobile
      ? this.helpersService.toVW(type?.top || 0, this.mobileLayout)
      : this.isTablet
      ? this.helpersService.toVW(type?.top || 0, this.tabletBreakpoint)
      : this.helpersService.toREM(type?.top || 0);
    this.renderer.setStyle(this.sillhouette?.nativeElement, 'top', top);

    let left = this.isMobile
      ? this.helpersService.toVW(type?.left || 0, this.mobileLayout)
      : this.isTablet
      ? this.helpersService.toVW(type?.left || 0, this.tabletBreakpoint)
      : this.helpersService.toREM((type?.left || 0) + this.offsetX);
    this.renderer.setStyle(this.sillhouette?.nativeElement, 'left', left);
  }

  onWindowResize() {
    //validate platform
    if (!this.helpersService.validatePlatform()) return;

    let newIsMobile = this.helpersService.isMobile(this.tabletBreakpoint);
    let newIsMobileLayout = this.helpersService.isMobile(this.mobileLayout);
    let newIsTablet = this.helpersService.isMobile(this.desktopBreakpoint);
    let newIsDesktopXLgLayout = this.helpersService.isMobile(
      this.desktopLgBreakpoint
    );
    let newIsDesktopLgLayout = this.helpersService.isMobile(
      this.desktopXLBreakpoint
    );

    //if breakpoint changed, reload imgs
    if (
      this.isMobile != newIsMobile ||
      this.isMobileLayout != newIsMobileLayout ||
      this.isTablet != newIsTablet ||
      this.isDesktopXLgLayout != newIsDesktopXLgLayout ||
      this.isDesktopLgLayout != newIsDesktopLgLayout
    ) {
      this.isMobile = newIsMobile;
      this.isMobileLayout = newIsMobileLayout;
      this.isTablet = newIsTablet;
      this.isDesktopXLgLayout = newIsDesktopXLgLayout;
      this.isDesktopLgLayout = newIsDesktopLgLayout;

      //reload slide imgs
      this.loadSlide();

      //set up sillhouette
      this.setSillhouetteProps();
    }
  }
}
